import apiClient, { retryRequest } from "./axios";
import dayjs from "dayjs";
import { processDayjsObjects } from "./apiUtils";

// 航班计划相关API
export const scheduleApi = {
    // 获取计划列表
    getScheduleList: async (params = {}) => {
        const response = await apiClient.get("/v1/schedules", { params });
        return response;
    },

    // 轻量计划列表（下拉选择用，单次请求返回全量）
    getScheduleListSimple: async (params = {}) => {
        const response = await apiClient.get("/v1/schedules/simple", { params });
        return response;
    },

    // 获取单个计划
    getScheduleById: async (id) => {
        const response = await apiClient.get(`/v1/schedules/${id}`);
        return response;
    },

    // 发布计划到运行层
    publishToOperations: async (scheduleId, data = {}) => {
        try {
            const response = await apiClient.post(`/v1/schedules/${scheduleId}/publish`, data);
            return response;
        } catch (error) {
            console.error("发布计划到运行层失败:", error);
            throw error;
        }
    },

    // 取消发布计划（回退到航司确认）
    unpublishSchedule: async (scheduleId) => {
        try {
            const response = await apiClient.post(`/v1/schedules/${scheduleId}/unpublish`);
            return response;
        } catch (error) {
            console.error("取消发布计划失败:", error);
            throw error;
        }
    },

    // 获取计划详情（包含航班信息）
    getScheduleDetail: async (id, options = {}) => {
        try {
            // 获取计划主体信息
            const scheduleResponse = await apiClient.get(`/v1/schedules/${id}`);
            const scheduleData = scheduleResponse.data; // 从 .data 获取计划数据

            // 并行获取关联数据
            const [aircraftResponse, parameterResponse, flightsResponse] = await Promise.all([
                apiClient.get(`/v1/aircraft/${scheduleData.aircraft_id}`), // 使用 scheduleData 中的 id
                scheduleData.parameter_id // 如果 parameter_id 存在才获取
                    ? apiClient.get(`/v1/schedule-parameters/${scheduleData.parameter_id}`)
                    : Promise.resolve({ data: null }), // 否则返回包含 null 数据的 resolved Promise
                apiClient.get(`/v1/schedules/${id}/flights`, {
                    params: {
                        page: 1, // 始终请求第一页
                        page_size: 300, // 固定使用较大的page_size值获取足够多的航班
                    },
                }),
            ]);

            const aircraftData = aircraftResponse.data; // 从 .data 获取飞机数据
            const parameterData = parameterResponse.data; // 从 .data 获取参数数据
            const flightsData = flightsResponse.data; // 从 .data 获取航班数据

            // 组合最终返回的数据结构
            const combinedData = {
                ...scheduleData,
                aircraft: aircraftData || {}, // 合并飞机信息
                parameter: parameterData || {}, // 合并参数信息
                flights: flightsData.items || [], // 合并航班列表
                flights_total: flightsData.total || 0, // 合并航班总数
            };

            scheduleResponse.data = combinedData;

            // 返回修改后的 AxiosResponse 对象
            return scheduleResponse;
        } catch (error) {
            console.error(`获取计划详情 ID=${id} 失败:`, error);
            if (error.response && error.response.status === 404) {
                console.warn(`计划 ID=${id} 未找到`);
            }
            throw error;
        }
    },

    // 根据飞机注册号获取当前执行计划
    getCurrentScheduleByAircraft: async (registration, dateRange = {}) => {
        try {
            // 构建查询参数
            const params = {
                aircraft_reg: registration,
                status: "PUBLISHED", // 只查询已发布状态
                include_flights: true, // 包含航班信息
            };

            // 如果提供了日期范围，添加到查询参数
            if (dateRange.startDate) {
                params.start_date = typeof dateRange.startDate === "string" ? dateRange.startDate : dayjs(dateRange.startDate).format("YYYY-MM-DD");
            }
            if (dateRange.endDate) {
                params.end_date = typeof dateRange.endDate === "string" ? dateRange.endDate : dayjs(dateRange.endDate).format("YYYY-MM-DD");
            }

            // 请求API获取当前计划
            const response = await apiClient.get("/v1/schedules/current", { params });

            return response;
        } catch (error) {
            console.error("获取当前执行计划失败:", error);
            throw error;
        }
    },

    // 创建计划
    createSchedule: async (data) => {
        // 处理可能的Dayjs对象格式，并深拷贝数据
        const processedData = processDayjsObjects({ ...data });

        // 确保 tags 字段是数组类型
        if (processedData.tags && !Array.isArray(processedData.tags)) {
            processedData.tags = [processedData.tags];
        }

        const response = await apiClient.post("/v1/schedules", processedData);
        return response;
    },

    // 更新计划
    updateSchedule: async (id, data) => {
        // 处理可能的Dayjs对象格式，并深拷贝数据
        const processedData = processDayjsObjects({ ...data });

        const response = await apiClient.put(`/v1/schedules/${id}`, processedData);
        return response; // 直接返回 AxiosResponse
    },

    // 删除计划
    deleteSchedule: async (id) => {
        const response = await apiClient.delete(`/v1/schedules/${id}`);
        return response; // 直接返回 AxiosResponse
    },

    // 获取草案列表
    getDraftList: (params) => {
        return apiClient.get("/v1/schedules", { params: { ...params, status: "DRAFT" } });
    },

    // 提交草案审批 - 更新方法名
    submitDraftForApproval: (id) => {
        return apiClient.put(`/v1/schedules/${id}/status`, { status: "SUBMITTED" });
    },

    // 添加状态变更方法
    updateScheduleStatus: (id, status) => {
        return apiClient.put(`/v1/schedules/${id}/status`, { status });
    },

    // 复制计划
    duplicateSchedule: async (id, options = {}) => {
        // 处理可能的Dayjs对象格式
        const processedOptions = processDayjsObjects({ ...options });

        const response = await apiClient.post(`/v1/schedules/${id}/duplicate`, processedOptions);
        return response;
    },

    // 获取计划参数列表
    getScheduleParameters: async (params = {}) => {
        const response = await apiClient.get("/v1/schedule-parameters", { params });
        return response;
    },

    // 获取单个计划参数
    getScheduleParameterById: async (paramId) => {
        // 使用重试机制封装API调用
        return await retryRequest(
            () => apiClient.get(`/v1/schedule-parameters/${paramId}`),
            3, // 最大重试3次
            1000, // 初始延迟1秒
            (error) => {
                // 网络错误或服务器错误时重试
                return !error.response || error.response.status >= 500;
            },
        );
    },

    // 获取计划航班列表
    getScheduleFlights: async (scheduleId, options = {}) => {
        // 从options中获取分页参数，使用默认值
        const {
            page = 1,
            page_size = 500, // 使用较大的默认值获取足够多的航班
        } = options;

        // 构建请求参数
        const params = {
            ...options,
            page,
            page_size,
        };

        const response = await apiClient.get(`/v1/schedules/${scheduleId}/flights`, { params });
        return response; // 直接返回 AxiosResponse
    },

    // 获取可导入到 V1 编辑器的 V2 EDITING 轨来源摘要
    getV2TrackImportSources: async (scheduleId) => {
        const response = await apiClient.get(`/v1/schedules/${scheduleId}/import-sources/v2-tracks`);
        return response;
    },

    // 只读预览 V2 EDITING 轨导入 V1 编辑器的行数据
    previewV2TrackImport: async (scheduleId, payload) => {
        const response = await apiClient.post(`/v1/schedules/${scheduleId}/import-sources/v2-tracks/preview`, payload);
        return response;
    },

    // 创建计划参数
    createScheduleParameter: async (data) => {
        const response = await apiClient.post("/v1/schedule-parameters", data);
        return response;
    },

    // 更新计划参数
    updateScheduleParameter: async (id, data) => {
        const response = await apiClient.put(`/v1/schedule-parameters/${id}`, data);
        return response;
    },

    // 计算航线数据
    calculateRoutes: async (data = {}) => {
        const response = await apiClient.post(`/v1/routes/calculate`, data);
        return response;
    },

    // 批量保存计划航班（先删除再创建，简化操作）
    saveScheduleFlights: async (scheduleId, flightData, options = {}) => {
        // 确保scheduleId是一个有效值
        if (!scheduleId || typeof scheduleId === "object") {
            console.error("Invalid scheduleId:", scheduleId);
            throw new Error("Invalid scheduleId");
        }

        // 检查传入的数据格式
        if (!Array.isArray(flightData)) {
            throw new Error("航班数据必须是数组");
        }

        // 确保每个航班都有正确的schedule_id
        const processedFlightData = flightData.map((flight) => ({
            ...flight,
            schedule_id: scheduleId, // 强制设置正确的schedule_id
        }));

        // 预检查数据有效性
        for (let i = 0; i < processedFlightData.length; i++) {
            const flight = processedFlightData[i];
            // 验证必要字段
            const requiredFields = ["flight_number", "departure_airport", "arrival_airport", "std", "sta"];
            for (const field of requiredFields) {
                if (!flight[field]) {
                    console.error(`航班 #${i + 1} 缺少必要字段: ${field}`, flight);
                }
            }

            // 验证时间字段
            if (flight.std && flight.sta) {
                const stdDate = new Date(flight.std);
                const staDate = new Date(flight.sta);
                if (stdDate >= staDate) {
                    console.error(`航班 #${i + 1} 起飞时间不能晚于或等于到达时间`, {
                        std: flight.std,
                        sta: flight.sta,
                        flight,
                    });
                }
            }
        }

        try {
            // 调用新的批量保存端点
            const config = { params: {} };
            // 如果有忽略路由验证的选项，添加到查询参数中
            if (options && options.ignore_route_validation) {
                config.params.ignore_route_validation = true;
            }
            if (options && options.acknowledgeExecutedRouteRevoke) {
                config.params.acknowledge_executed_route_revoke = true;
            }

            let body;
            if (options?.scheduleAdditionalInfoMerge) {
                body = {
                    flights: processedFlightData,
                    schedule_additional_info_merge: options.scheduleAdditionalInfoMerge,
                };
            } else {
                body = processedFlightData;
            }

            const response = await apiClient.post(`/v1/schedules/${scheduleId}/save-flights`, body, config);
            return response;
        } catch (error) {
            // 【航段航路BH优化】移除冗余日志，由上层组件统一处理错误显示
            // 422 错误通常包含具体的验证错误信息
            if (error.response?.status === 422 && error.response.data) {
                const errorData = error.response.data;
                let errorMsg = "数据验证失败";

                if (errorData.errors && Array.isArray(errorData.errors)) {
                    errorMsg += `: ${errorData.errors.length} 个错误`;
                } else if (errorData.detail) {
                    errorMsg += `: ${errorData.detail}`;
                }

                throw new Error(errorMsg);
            }

            // 直接抛出错误，由上层处理
            throw error;
        }
    },

    // 获取正式计划列表（非草稿状态）
    getOfficialScheduleList: async (params = {}) => {
        // 确保只查询非草稿状态的计划
        const officialParams = { ...params };

        // 如果没有明确设置状态，默认查询所有非草稿状态
        // 根据API规范，多值参数必须使用逗号分隔的字符串
        if (!officialParams.status) {
            officialParams.status = "SUBMITTED,CONFIRMATION_PENDING,AIRLINE_CONFIRMED,PUBLISHED,DEPRECATED";
        }

        // 如果状态是数组，转换为逗号分隔的字符串
        if (Array.isArray(officialParams.status)) {
            officialParams.status = officialParams.status.join(",");
        }

        // 如果飞机ID是数组，转换为逗号分隔的字符串
        if (Array.isArray(officialParams.aircraft_id)) {
            officialParams.aircraft_id = officialParams.aircraft_id.join(",");
        }

        // 请求API
        const response = await apiClient.get("/v1/schedules", { params: officialParams });
        return response;
    },

    getIngestOriginScheduleList: async (params = {}) => {
        const response = await apiClient.get("/v1/schedules/ingest-origin", { params });
        return response;
    },

    // 创建计划航班
    createScheduleFlight: async (scheduleId, flightData) => {
        const response = await apiClient.post(`/v1/schedules/${scheduleId}/flights`, flightData);
        return response; // 直接返回 AxiosResponse
    },

    // 获取计划航班详情
    getScheduleFlightById: async (scheduleId, flightId) => {
        const response = await apiClient.get(`/v1/schedules/${scheduleId}/flights/${flightId}`);
        return response; // 直接返回 AxiosResponse
    },

    // 更新计划航班
    updateScheduleFlight: async (scheduleId, flightId, flightData) => {
        const response = await apiClient.put(`/v1/schedules/${scheduleId}/flights/${flightId}`, flightData);
        return response; // 直接返回 AxiosResponse
    },

    // 获取计划活动时间线
    getScheduleActivityTimeline: async (scheduleId, params = {}) => {
        if (!scheduleId) return { data: { items: [], total: 0 } };
        try {
            // 确保使用标准分页参数
            const paginationParams = {
                page: params.page || 1,
                page_size: params.page_size || 50,
                ...params,
            };
            const response = await apiClient.get(`/v1/schedules/${scheduleId}/activity-timeline`, { params: paginationParams });
            return response;
        } catch (error) {
            console.error("获取计划活动时间线失败:", error);
            throw error;
        }
    },
};
